import type { ObjectId } from 'mongodb';

import { ProgramType } from './ProgramTypes';

export enum FinanceType {
  lease = 'lease',
  ppa = 'ppa',
}

export enum FinanceTypeLabels {
  lease = 'Lease',
  ppa = 'PPA',
}

export enum ProductStatus {
  active = 'active',
  archived = 'archived',
  customInterpolated = 'customInterpolated',
  deprecated = 'deprecated',
}

export interface PpaPricePerWattOption {
  productionFactor: number;
  ppw: number;
}

export interface BaseProduct {
  id: ObjectId;
  priceSheetId: string | ObjectId;
  organizationId?: string;
  programType: ProgramType;
  name: string;
  description?: string;
  status: ProductStatus;
  deprecationDate?: Date;
  escalationRate: number;
}

export interface SolarProductDocument extends BaseProduct {
  inboundPaymentProviderProductId?: string;
  type?: FinanceType;
  lseId?: number;
  utilityName?: string;
  state?: string;
  kwhRate: number;
  ppwRateOptions: Array<PpaPricePerWattOption>;
}

export interface DoePrProductDocument extends BaseProduct {
  ppwRate: number;
  monthlyPayment: number;
  minSystemSize: number;
  maxSystemSize: number;
  minBatteryCapacity: number;
  maxBatteryCapacity: number;
}

export interface ConsumerSolarProductDocument extends BaseProduct {
  ppwRate: number;
  kwhRate: number;
  minSystemSize: number;
  maxSystemSize: number;
  minProductionFactor: number;
  maxProductionFactor: number;
}

export interface EquipmentProductDocument extends BaseProduct {
  term: number;
  paymentFactor: number;
  minSystemCost: number;
  maxSystemCost: number;
}

export type ProductDocument<T extends ProgramType> = T extends 'solar'
  ? SolarProductDocument
  : T extends 'doePr'
    ? DoePrProductDocument
    : T extends 'consumerSolar'
      ? ConsumerSolarProductDocument
      : T extends 'hvac'
        ? EquipmentProductDocument
        : T extends 'energyBackup'
          ? EquipmentProductDocument
          : T extends 'waterHeater'
            ? EquipmentProductDocument
            : T extends 'generator'
              ? EquipmentProductDocument
              : T extends 'geothermal'
                ? EquipmentProductDocument
                : SolarProductDocument;

export interface PaymentSchedule {
  year: number;
  monthlyPayment: number;
  batteryPayment?: number;
  totalMonthlyPayment: number;
}
export interface ProductPricingResponse {
  productId: ObjectId;
  type?: string;
  name: string;
  description?: string;
  lseId?: number;
  utilityName?: string;
  state?: string;
  escalationRate: number;
  kwhRate: number;
  ppwRate: number;
  effectivePpwRate: number;
  solarOnlyEPCPayment: number;
  totalEPCPayment: number;
  batteryCost?: number;
  monthlyPayments: Array<PaymentSchedule>;
  programType: ProgramType;
  resultsLimitedByMonthlyPaymentCap?: boolean;
}

export interface HVACProductPricingResponse {
  productId: ObjectId;
  type?: string;
  name: string;
  description?: string;
  lseId?: number;
  utilityName?: string;
  state?: string;
  escalationRate: number;
  monthlyPayments: Array<PaymentSchedule>;
  programType: ProgramType;
  resultsLimitedByMonthlyPaymentCap?: boolean;
  term: number;
  isEstimate?: boolean;
}

export type EnergyBackupProductPricingResponse = {
  productId: ObjectId;
  type?: string;
  name: string;
  description?: string;
  lseId?: number;
  utilityName?: string;
  state?: string;
  escalationRate: number;
  monthlyPayments: Array<PaymentSchedule>;
  programType: ProgramType;
  resultsLimitedByMonthlyPaymentCap?: boolean;
  term: number;
};

export interface GeothermalProductPricingResponse {
  productId: ObjectId;
  type?: string;
  name: string;
  description?: string;
  state?: string;
  escalationRate: number;
  monthlyPayments: Array<PaymentSchedule>;
  programType: ProgramType;
  resultsLimitedByMonthlyPaymentCap?: boolean;
  term: number;
  totalEPCPayment: number;
}

export interface WaterHeaterProductPricingResponse {
  productId: ObjectId;
  type?: string;
  name: string;
  description?: string;
  lseId?: number;
  utilityName?: string;
  state?: string;
  escalationRate: number;
  monthlyPayments: Array<PaymentSchedule>;
  programType: ProgramType;
  resultsLimitedByMonthlyPaymentCap?: boolean;
  term: number;
  isEstimate?: boolean;
}

export interface GeneratorProductPricingResponse {
  productId: ObjectId;
  type?: string;
  name: string;
  description?: string;
  lseId?: number;
  utilityName?: string;
  state?: string;
  escalationRate: number;
  monthlyPayments: Array<PaymentSchedule>;
  programType: ProgramType;
  resultsLimitedByMonthlyPaymentCap?: boolean;
  term: number;
  isEstimate?: boolean;
}

export interface CustomMonthlyPaymentPricingLog {
  id: ObjectId;
  organizationId: string;
  priceSheetMappingId: ObjectId;
  productParameters: string;
  targetMonthlyPayment: number;
  state: string;
  lseId: number;
  systemSizeKw: number;
  systemFirstYearProductionKwh: number;
  productionFactor: number;
  kwhRatePosition: number;
  kwhDifference: number;
  ppwDifference: number;
  lowKwhRate: number;
  highKwhRate: number;
  rawKwhRate: number;
  lowPpw: number;
  highPpw: number;
  lowProductId: ObjectId;
  highProductId: ObjectId;
  matchingProductId?: ObjectId;
  tempProduct: {
    priceSheetId: ObjectId | string;
    programType: ProgramType;
    name: string;
    status: ProductStatus;
    escalationRate: number;
    kwhRate: number;
    ppwRateOptions: Array<PpaPricePerWattOption>;
  };
}
